import React, { useCallback, useEffect } from 'react';
import Textarea from '~/app/modules/form/Textarea';
import MForm from '~/app/modules/form';
import classes from './style.module.scss';
import useTranslation from '~/app/hooks/useTranslation';
import Select from '~/app/modules/form/Select';
import useForm from "~/app/modules/form/useForm";
import { IssueDetails, IssuePayload } from "~/types";
import { AddIssueProps } from "~/issues/Add";
import Category from "~/issues/Add/Form/Details/Category";
import Attachments from "~/issues/Add/Form/Details/Attachments";
import { useIssueSortingTypes, useIssueTypes } from "~/app/data/statuses/issue";
import { useStoreActions, useStoreState } from "store/index";
import moment from 'moment';
import Date from '~/app/modules/form/Date';
import AccessControl from '~/accessControl';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';

export interface DetailsProps {
    setShowDetails: (boolean) => void
    type: AddIssueProps['type']
    issueDetails?: IssueDetails
}

export type DetailsComponent = React.FC<DetailsProps>;

const Details: DetailsComponent = ({ setShowDetails, type, issueDetails }) => {
    const { t } = useTranslation('issue');
    const { values, fieldEvents } = useForm<IssuePayload>({ formName: 'addIssue' });
    const issueTypes = useIssueTypes();
    const sortingTypes = useStoreState(state => state.issue.sortingTypes);
    const fetchIssueSortingTypes = useStoreActions(actions => actions.issue.fetchIssueSortingTypes);
    const fetchUsers = useStoreActions(actions => actions.app.appData.fetchUsers);
    const fetchDepartments = useStoreActions(actions => actions.app.appData.fetchDepartments);
    const issueSortingTypes = useIssueSortingTypes(sortingTypes);
    const appUsers = useStoreState(state => state.app.appData.users);
    const appDepartments = useStoreState(state => state.app.appData.departments);
    const users = appUsers.map(user => ({ text: user.name, value: user.id }));
    const departments = appDepartments.map(department => ({
        text: department.name,
        value: department.id
    }));

    useEffect(() => {
        if (sortingTypes.length === 0) {
            fetchIssueSortingTypes();
        }
        if (appUsers.length === 0) {
            fetchUsers();
        }
        if (appDepartments.length === 0) {
            fetchDepartments();
        }
    }, [
        sortingTypes.length,
        appUsers.length,
        appDepartments.length,
        fetchIssueSortingTypes,
        fetchUsers,
        fetchDepartments,
    ]);
    // When only one department is available, preselect it so the user doesn't have to open the dropdown.
    const singleDepartmentId = departments.length === 1 ? departments[0].value : 0;
    const displayTypeInIssues = useStoreState(state => state.machine.displayTypeInIssues);
    const renderLabel = (label: string,required = false,requiredClassName?: string) => (
        <label>
          {label}
          {required && <span className={requiredClassName}>*</span>}
        </label>
      );

    const handleImagesChange = useCallback((images: (string | File)[]) => {
        fieldEvents.onChange({ target: { name: 'images', value: images } } as any);
    }, [fieldEvents]);

    const handleFilesChange = useCallback((files: (string | File)[]) => {
        fieldEvents.onChange({ target: { name: 'files', value: files } } as any);
    }, [fieldEvents]);

    return (
        <MForm className={`${classes['form']} ${classes['details']}`}>
            {issueDetails && (
                <div className={classes['issue-register-details']}>
                    {
                        moment(issueDetails?.createdAt).isSame(issueDetails?.updatedAt) ? (
                            t('registerTimeName', {
                                date: issueDetails?.registrationDate,
                                name: issueDetails?.issueCreatedBy
                            })
                        ) : (
                            t('updateTimeName', {
                                date: issueDetails?.updatedAt,
                                name: issueDetails?.issueUpdatedBy
                            })
                        )
                    }
                </div>
            )}
            <Category setShowDetails={setShowDetails} />
            {!issueDetails?.id && <AccessControl requiredPermissions={PERMISSION_GROUPS.setIssueRegistrationDate} >
                <Date
                    className={`${classes['input-container']}`}
                    label={renderLabel('Date',true,classes['required-label'])}
                    strongTime
                    placeholder={'dd.MM.yyyy HH:mm'}
                    name="registrationDate"
                    format={'DD.MM.YYYY HH:mm'}
                    defaultValue={values?.registrationDate}
                    onChange={fieldEvents.onChange}
                    max={moment().endOf('day').format('YYYY-MM-DDTHH:mm')}
                />
            </AccessControl>}
            {type === 'issue' && <Select
                className={`${classes['input-container']}`}
                label={renderLabel(t('classification.title'),true,classes['required-label'])}
                name="type"
                placeholder={t('classification.placeholder')}
                options={issueTypes}
                icon={'keyboard_arrow_down'}
                onChange={fieldEvents.onChange}
                defaultValue={values?.type}
            />}
            {displayTypeInIssues && type === 'issue' && <Select
                className={`${classes['input-container']}`}
                label={renderLabel(t('sortingType.title'),true,classes['required-label'])}
                name="sortingType"
                placeholder={t('sortingType.placeholder')}
                options={issueSortingTypes}
                icon={'keyboard_arrow_down'}
                onChange={fieldEvents.onChange}
                defaultValue={values?.sortingType}
                showEmptyOption={true}
                emptyOptionValue={0}
            />}
            <Textarea
                className={`${classes['input-container']}`}
                label={renderLabel(t('description.title'),true,classes['required-label'])}
                placeholder={t('description.placeholder')}
                descriptionPosition="top"
                name="description"
                defaultValue={values?.description}
                {...fieldEvents}
            />
            {type === 'issue' && <Select
                className={`${classes['input-container']}`}
                label={renderLabel(t('department.title'),true,classes['required-label'])}
                name="departmentId"
                placeholder={t('department.placeholder')}
                options={departments}
                icon={'keyboard_arrow_down'}
                onChange={fieldEvents.onChange}
                defaultValue={values?.departmentId || singleDepartmentId}
                showEmptyOption={true}
                emptyOptionValue={0}
            />}
            {type === 'repair' && <Select
                className={`${classes['input-container']}`}
                label={renderLabel(t('user.title'),true,classes['required-label'])}
                name="performedBy"
                placeholder={t('user.placeholder')}
                options={users}
                icon={'keyboard_arrow_down'}
                onChange={fieldEvents.onChange}
                defaultValue={values?.performedBy}
            />}
            <Attachments
                onImagesChange={handleImagesChange}
                onFilesChange={handleFilesChange}
                defaultImages={values?.images as (string | File)[] | undefined}
                defaultFiles={values?.files as (string | File)[] | undefined}
            />
        </MForm>
    );
};

export default Details
